/**
 * Roles created in the Role Manager before 2026-10-27 belong to one organization; new ones are system
 * roles (`buildSystemRoleKey`). IAM roles are project-wide
 * (every organization gets a copy) and keep no organization of their own, so the organization is in
 * the key: `org::<organization-slug>::<role-slug>`. Each organization lists only its own, and two
 * organizations can each have their own "Team Lead".
 *
 * Three kinds of roles exist side by side:
 * - organization roles (`org::...`), managed in the Role Manager;
 * - Head of Department roles (`hrm::department-scope::...`), one per department, managed with the
 *   department (see department-scope.ts);
 * - system roles (admin, app_user, head_of_business, super_admin, ... and the ones created in the
 *   Role Manager), offered in every organization and copied to the other environments by the
 *   sync-roles skill.
 */
import { isDepartmentRole, organizationKeySegment, toKeySegment } from './department-scope';

export const ORGANIZATION_ROLE_PREFIX = 'org::';

const ROLE_SEGMENT_MAX = 60;

/** The key prefix of one organization's roles. */
export const organizationRolePrefixOf = (organizationName: string, organizationId = '') =>
  `${ORGANIZATION_ROLE_PREFIX}${organizationKeySegment(organizationName, organizationId)}::`;

/** A new system role's key, from its name, in the style of the existing ones (`team_lead`); an empty
 * name gives an empty key. */
export const buildSystemRoleKey = (name: string) =>
  toKeySegment(name, ROLE_SEGMENT_MAX).replace(/-/g, '_');

export const isOrganizationRole = (slug: string) => slug.startsWith(ORGANIZATION_ROLE_PREFIX);

export const isSystemRole = (slug: string) => !isOrganizationRole(slug) && !isDepartmentRole(slug);

/** Is this role offered in that organization: a system role, or one of its own roles? Head of
 * Department roles are decided by the caller, from the organization's departments. */
export const isRoleOfOrganization = (slug: string, organizationName: string, organizationId = '') =>
  isSystemRole(slug) || slug.startsWith(organizationRolePrefixOf(organizationName, organizationId));
